/**
 * Master Admin — Property Articles tab.
 *
 * Lists every article the Property Article module has generated (manually or
 * by the hourly scheduler) with delete + regenerate actions. Also exposes a
 * "Run schedulers now" button that fires the same cron logic on demand.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { getApiUrl } from "@/utils/api";
import { Button } from "@/components/ui/button";
import { Loader2, Trash2, RefreshCw, Sparkles, Clock, ExternalLink } from "lucide-react";
import { useLockBodyScroll } from "@/hooks/useLockBodyScroll";
import { pollAiJob } from "@/utils/pollAiJob";

const API = getApiUrl();

function formatWhen(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

export default function PropertyArticlesAdminTab() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [runningNow, setRunningNow] = useState(false);
  useLockBodyScroll(!!preview);

  const fetchArticles = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/property-articles`, {
        params: { limit: 100 },
        withCredentials: true,
      });
      setArticles(data?.items || []);
    } catch {
      setArticles([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchArticles();
  }, []);

  const handleDelete = async (article_id) => {
    if (!confirm("Delete this article? This can't be undone.")) return;
    setBusyId(article_id);
    try {
      await axios.delete(`${API}/property-articles/${article_id}`, {
        withCredentials: true,
      });
      toast.success("Article deleted");
      setArticles((cur) => cur.filter((a) => a.article_id !== article_id));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally {
      setBusyId(null);
    }
  };

  const handleRegenerate = async (article_id) => {
    setBusyId(article_id);
    try {
      const { data } = await axios.post(
        `${API}/property-articles/${article_id}/regenerate`,
        {},
        { withCredentials: true }
      );
      const article = await pollAiJob(data.job_id);
      toast.success(`Regenerated: "${article.title}"`);
      // Replace in state; move to top since generated_at is newer.
      setArticles((cur) => {
        const rest = cur.filter((a) => a.article_id !== article_id);
        return [article, ...rest];
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Regeneration failed");
    } finally {
      setBusyId(null);
    }
  };

  const handleRunSchedulers = async () => {
    setRunningNow(true);
    try {
      const { data } = await axios.post(
        `${API}/property-articles/run-schedulers-now`,
        {},
        { withCredentials: true }
      );
      toast.success(`Schedulers: ${data.ran} ran, ${data.skipped} skipped`);
      await fetchArticles();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to trigger schedulers");
    } finally {
      setRunningNow(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="property-articles-admin">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold">Property Articles</h3>
          <p className="text-xs text-muted-foreground">
            Every article generated by any Property Article module instance —
            manually or by the hourly auto-run scheduler.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={fetchArticles}
            data-testid="articles-refresh"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button
            type="button"
            onClick={handleRunSchedulers}
            disabled={runningNow}
            data-testid="articles-run-schedulers"
          >
            {runningNow ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4 mr-2" />
            )}
            Run schedulers now
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="p-8 text-center text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin mx-auto mb-2" />
          Loading articles…
        </div>
      ) : articles.length === 0 ? (
        <div className="p-8 text-center text-sm text-muted-foreground border border-dashed rounded">
          No articles yet. Generate one from the CMS → Modules → Property
          Article, or click "Run schedulers now" if you have an enabled
          scheduler.
        </div>
      ) : (
        <div className="border border-border rounded overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="text-left px-3 py-2">Title</th>
                <th className="text-left px-3 py-2 w-32">Source</th>
                <th className="text-left px-3 py-2 w-48">Generated</th>
                <th className="text-right px-3 py-2 w-48">Actions</th>
              </tr>
            </thead>
            <tbody>
              {articles.map((a) => (
                <tr key={a.article_id} className="border-t border-border hover:bg-muted/30">
                  <td className="px-3 py-2">
                    <button
                      className="text-left font-medium hover:underline"
                      onClick={() => setPreview(a)}
                      data-testid={`article-title-${a.article_id}`}
                    >
                      {a.title}
                    </button>
                    <div className="text-xs text-muted-foreground line-clamp-1">
                      {a.theme}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-xs">
                    <span
                      className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium ${
                        a.source === "scheduled"
                          ? "bg-blue-50 text-blue-800 border border-blue-200"
                          : "bg-emerald-50 text-emerald-800 border border-emerald-200"
                      }`}
                    >
                      {a.source || "manual"}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">
                    <Clock className="inline h-3 w-3 mr-1" />
                    {formatWhen(a.generated_at)}
                  </td>
                  <td className="px-3 py-2 text-right space-x-1">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleRegenerate(a.article_id)}
                      disabled={busyId === a.article_id}
                      data-testid={`article-regen-${a.article_id}`}
                    >
                      {busyId === a.article_id ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <RefreshCw className="h-3 w-3" />
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setPreview(a)}
                      data-testid={`article-preview-${a.article_id}`}
                    >
                      <ExternalLink className="h-3 w-3" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-destructive hover:text-destructive"
                      onClick={() => handleDelete(a.article_id)}
                      disabled={busyId === a.article_id}
                      data-testid={`article-delete-${a.article_id}`}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {preview && (
        <div
          className="fixed inset-0 z-[120] bg-black/70 overflow-y-auto"
          onClick={(e) => { if (e.target === e.currentTarget) setPreview(null); }}
        >
          <div className="relative mx-auto my-8 w-[92vw] max-w-3xl bg-background rounded-md shadow-2xl p-6">
            <button
              onClick={() => setPreview(null)}
              className="absolute top-3 right-3 h-8 w-8 rounded-full border hover:bg-muted"
            >
              ×
            </button>
            <div className="mb-4">
              <p className="text-xs text-muted-foreground flex items-center gap-1 mb-2">
                <Clock className="h-3 w-3" /> {formatWhen(preview.generated_at)}
              </p>
              <h2 className="text-2xl font-serif mb-1">{preview.title}</h2>
              <p className="text-xs text-muted-foreground">Theme: {preview.theme}</p>
            </div>
            <article
              className="prose max-w-none prose-img:rounded-md prose-img:my-4"
              dangerouslySetInnerHTML={{ __html: preview.content_html }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
